import React, { useCallback, useEffect, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import {
  ArrowUpRight,
  Check as Tick,
  ChevronDown,
  KeyRound,
  Lock,
  Printer,
} from "lucide-react";
import { Pill } from "@/components/site/Reveal";
import ShaderBackground from "@/components/site/ShaderBackground";
import { AUDIT_STEPS } from "@/lib/audit";
import { AUDIT } from "@/lib/check";
import {
  clearFresh,
  forgetRoom,
  isFresh,
  legacyCode,
  remember,
  standing,
} from "@/lib/room";
import Host, { HOST } from "@/components/site/Host";
import RoomFiles from "@/pages/RoomFiles";
import AuditDoc, { docPages } from "@/components/site/AuditDoc";
import { pauseTracking } from "@/lib/track";

const two = (n) => String(n).padStart(2, "0");
const day = (iso, year = true) =>
  Number.isNaN(Date.parse(iso))
    ? String(iso || "")
    : new Date(iso).toLocaleDateString("en-GB", {
        day: "numeric",
        month: "long",
        ...(year && { year: "numeric" }),
        timeZone: "UTC",
      });
// A day in the reader's own calendar, for dates the page worked out there.
const ownDay = (date) =>
  date.toLocaleDateString("en-GB", { day: "numeric", month: "long" });
const stamp = (iso) =>
  `${new Date(iso).toLocaleString("en-GB", {
    day: "numeric",
    month: "long",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    timeZone: "UTC",
  })} UTC`;
// Who on the client's side wrote something: you, or a named colleague.
const whose = (by, me) => (!by || by === me?.email ? "You" : by.split("@")[0]);
const FIELD =
  "h-12 w-full rounded-[14px] bg-white px-4 text-[16px] outline-none ring-1 ring-black/10 placeholder:text-black/35 focus:ring-black";
const LABEL = "text-[11px] uppercase tracking-[0.1em]";

// Talk to the server about this page. The device is known by a sign-in cookie
// that the scripts here cannot read; nothing secret is kept in the page.
async function call(slug, action, payload) {
  const response = await fetch(`/api/room${action ? `/${action}` : ""}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ slug, ...payload }),
  });
  const body = await response.json().catch(() => ({}));
  if (!response.ok)
    throw Object.assign(new Error(body.error || ""), {
      status: response.status,
    });
  return body;
}

// The door. Email is the key: we send a link, the link signs the device in.
function Gate({ onLink, onCode, notice }) {
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [coding, setCoding] = useState(false);
  const [state, setState] = useState("idle");
  const [error, setError] = useState("");
  const run = (job) => async (e) => {
    e.preventDefault();
    if (state === "busy") return;
    setState("busy");
    setError("");
    try {
      setState((await job()) || "idle");
    } catch (err) {
      setError(
        err.status === 400 || err.status === 401 || err.status === 429
          ? err.message
          : "That did not work. Try again in a moment.",
      );
      setState("idle");
    }
  };
  const INPUT =
    "h-12 w-full min-w-0 shrink-0 rounded-full bg-white/10 px-6 text-[16px] text-white outline-none ring-1 ring-white/15 placeholder:text-white/30 focus:ring-white/60 sm:w-auto sm:flex-1";
  const GO =
    "h-12 shrink-0 rounded-full bg-white px-7 text-[14px] font-medium text-black transition-colors hover:bg-[#e6e6e6] disabled:opacity-60";
  return (
    <section
      className="relative flex min-h-screen items-center overflow-hidden bg-black px-5 pb-16 pt-28 text-white"
      data-testid="room-gate"
    >
      <ShaderBackground dark strength={0.2} lines={12} fadeLeft={0} />
      <div className="relative z-10 mx-auto w-full max-w-[1400px]">
        <Pill white>
          <Lock size={11} className="mr-1.5" /> Private
        </Pill>
        <h1 className="nm-title mt-6 text-[48px] leading-[0.98] sm:text-[84px]">
          Your Audit.
        </h1>
        {state === "sent" ? (
          <div className="mt-6 max-w-[520px]" data-testid="room-link-sent">
            <p className="text-[19px] leading-[1.35] text-white sm:text-[22px]">
              Check your inbox.
            </p>
            <p className="mt-3 text-[17px] leading-[1.45] text-white/65">
              If {email} belongs to this page, a sign-in link is on its way. It
              works once, for 15 minutes.
            </p>
            <button
              type="button"
              onClick={() => setState("idle")}
              className="font-sans nm-link mt-6 text-[14px] text-white/60"
            >
              Use another address
            </button>
          </div>
        ) : (
          <>
            <p className="mt-6 max-w-[520px] text-[19px] leading-[1.35] text-white/65 sm:text-[22px]">
              {notice ||
                "This page belongs to one client. Enter your work email and we send you a link that signs you in."}
            </p>
            <form
              className="mt-10 flex max-w-[520px] flex-col gap-2 sm:flex-row"
              onSubmit={run(async () => {
                await onLink(email.trim());
                return "sent";
              })}
            >
              <input
                aria-label="Your work email"
                placeholder="you@company.com"
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="room-email"
                className={INPUT}
              />
              <button
                type="submit"
                disabled={state === "busy" || !email.trim()}
                data-testid="room-link"
                className={GO}
              >
                {state === "busy" && !coding ? "Sending…" : "Email me a link"}
              </button>
            </form>
            {coding ? (
              <form
                className="mt-3 flex max-w-[520px] flex-col gap-2 sm:flex-row"
                onSubmit={run(() => onCode(code))}
              >
                <input
                  aria-label="Access code"
                  placeholder="XXXX-XXXX"
                  autoComplete="off"
                  autoCapitalize="characters"
                  spellCheck={false}
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  data-testid="room-code"
                  className={`${INPUT} uppercase tracking-[0.12em]`}
                />
                <button
                  type="submit"
                  disabled={state === "busy" || !code.trim()}
                  data-testid="room-open"
                  className={GO}
                >
                  Open
                </button>
              </form>
            ) : (
              <button
                type="button"
                onClick={() => setCoding(true)}
                className="font-sans nm-link mt-5 text-[14px] text-white/55"
                data-testid="room-use-code"
              >
                Have an access code instead?
              </button>
            )}
          </>
        )}
        {error && (
          <p
            role="alert"
            className="mt-4 text-[14px] text-red-300"
            data-testid="room-error"
          >
            {error}
          </p>
        )}
        <p className="mt-10 max-w-[460px] text-[13px] leading-[1.45] text-white/40">
          Trouble getting in? Write to hello@nomus.work and a person lets you
          in.
        </p>
      </div>
    </section>
  );
}

// Arriving by a link from a letter: one press signs this device in.
function Arrive({ onEnter }) {
  const [busy, setBusy] = useState(false);
  return (
    <section
      className="relative flex min-h-screen items-center overflow-hidden bg-black px-5 pb-16 pt-28 text-white"
      data-testid="room-arrive"
    >
      <ShaderBackground dark strength={0.2} lines={12} fadeLeft={0} />
      <div className="relative z-10 mx-auto w-full max-w-[1400px]">
        <Pill white>
          <Lock size={11} className="mr-1.5" /> Private
        </Pill>
        <h1 className="nm-title mt-6 text-[48px] leading-[0.98] sm:text-[84px]">
          Your Audit.
        </h1>
        <p className="mt-6 max-w-[520px] text-[19px] leading-[1.35] text-white/65 sm:text-[22px]">
          Your link is good. One press signs this device in for 60 days.
        </p>
        <button
          type="button"
          disabled={busy}
          onClick={() => {
            setBusy(true);
            onEnter();
          }}
          data-testid="room-enter"
          className="mt-10 h-12 rounded-full bg-white px-8 text-[14px] font-medium text-black transition-colors hover:bg-[#e6e6e6] disabled:opacity-60"
        >
          {busy ? "Signing in…" : "Sign in"}
        </button>
      </div>
    </section>
  );
}

// Top right of every page: who you are here, who else can come in, and the
// way out.
function Access({ room, onLock, onInvite }) {
  const [shown, setShown] = useState(false);
  const [email, setEmail] = useState("");
  const [state, setState] = useState("idle");
  const [error, setError] = useState("");
  const box = useRef(null);
  useEffect(() => {
    if (!shown) return undefined;
    const out = (e) => {
      if (!box.current?.contains(e.target)) setShown(false);
    };
    const esc = (e) => e.key === "Escape" && setShown(false);
    document.addEventListener("pointerdown", out);
    document.addEventListener("keydown", esc);
    return () => {
      document.removeEventListener("pointerdown", out);
      document.removeEventListener("keydown", esc);
    };
  }, [shown]);
  const invite = async (e) => {
    e.preventDefault();
    if (state === "busy" || !email.trim()) return;
    setState("busy");
    setError("");
    try {
      await onInvite(email.trim());
      setEmail("");
      setState("done");
    } catch (err) {
      setError(err.message || "That did not work. Try again in a moment.");
      setState("idle");
    }
  };
  const others = room.access.filter((a) => a !== room.me.email);
  return (
    <div ref={box} className="relative">
      <button
        type="button"
        onClick={() => setShown((v) => !v)}
        aria-expanded={shown}
        data-testid="room-access"
        className="inline-flex h-7 items-center gap-1.5 rounded-full bg-white/70 px-3 text-[12px] text-black/65 ring-1 ring-black/10 transition-colors hover:text-black"
      >
        <KeyRound size={11} /> Access
      </button>
      {shown && (
        <div
          className="absolute right-0 top-full z-20 mt-2 w-[min(360px,calc(100vw-40px))] rounded-[20px] bg-white p-5 text-black shadow-[0_24px_60px_-20px_rgba(0,0,0,0.5)]"
          data-testid="room-access-panel"
        >
          <p className={`${LABEL} opacity-45`}>Signed in as</p>
          <p className="mt-1 break-all text-[15px]" data-testid="room-me">
            {room.me.email || "The holder of the access code"}
          </p>
          {others.length > 0 && (
            <>
              <p className={`${LABEL} mt-4 opacity-45`}>
                {room.me.email
                  ? "Who else can open this page"
                  : "Who can open this page"}
              </p>
              <ul className="mt-1 space-y-0.5 text-[15px]">
                {others.map((a) => (
                  <li key={a} className="break-all">
                    {a}
                  </li>
                ))}
              </ul>
            </>
          )}
          {room.paid || (room.agreement && !room.agreement.signed) ? (
            <form onSubmit={invite} className="mt-4" data-testid="room-invite">
              <p className={`${LABEL} opacity-45`}>
                {room.paid
                  ? "Give a colleague access"
                  : "Someone else signs? Give them access"}
              </p>
              <div className="mt-2 flex gap-2">
                <input
                  aria-label="Colleague's work email"
                  placeholder="colleague@company.com"
                  type="email"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    setState("idle");
                  }}
                  className="h-10 min-w-0 flex-1 rounded-full bg-[#f7f7f7] px-4 text-[14px] outline-none ring-1 ring-black/10 placeholder:text-black/35 focus:ring-black"
                />
                <button
                  type="submit"
                  disabled={state === "busy" || !email.trim()}
                  className="h-10 shrink-0 rounded-full bg-black px-4 text-[12px] font-medium text-white transition-colors hover:bg-[#262626] disabled:opacity-40"
                >
                  {state === "busy" ? "Sending…" : "Give access"}
                </button>
              </div>
              <p className="mt-2 text-[12px] leading-[1.4] opacity-50">
                {error ||
                  (state === "done"
                    ? "Done. We have emailed them a link that signs them in."
                    : room.paid
                      ? "We email them a link that signs them in. Usually the person who does the job."
                      : "We email them a link to this page. One person, until the Audit has started.")}
              </p>
            </form>
          ) : (
            <p className="mt-4 text-[12px] leading-[1.4] opacity-50">
              Once the Audit has started you can give a colleague access here.
            </p>
          )}
          <div className="mt-5 flex items-center justify-between gap-4 border-t border-black/10 pt-4">
            <p className="text-[12px] leading-[1.4] opacity-50">
              This device stays signed in for 60 days.
            </p>
            <button
              type="button"
              onClick={onLock}
              data-testid="room-lock"
              className="inline-flex h-8 shrink-0 items-center gap-1.5 rounded-full px-3 text-[12px] ring-1 ring-black/20 transition-colors hover:bg-black hover:text-white"
            >
              <Lock size={11} /> Sign out
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// Right after booking: one line that says how they get back in. `how` is
// "mailed" when a sign-in link is in their inbox; otherwise it is the access
// code they were given because the email could not be sent.
function Way({ room, how, onKept }) {
  const [copied, setCopied] = useState(false);
  const mailed = how === "mailed";
  // Said once. In their inbox there is nothing more for them to do.
  useEffect(() => {
    if (mailed) onKept();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mailed]);
  const line =
    "mt-10 flex max-w-[720px] flex-wrap items-center gap-x-6 gap-y-3 border-t border-black/10 pt-5 text-[14px] leading-[1.45] text-black/60 print:hidden";
  if (mailed)
    return (
      <p className={line} data-testid="room-keep">
        <span className="flex items-start gap-2">
          <Tick size={13} className="mt-[3px] shrink-0" />
          <span>
            This device is signed in. For any other, we have emailed a sign-in
            link to <span className="text-black">{room.contact?.email}</span>;
            you can ask the page for a fresh one at any time.
          </span>
        </span>
      </p>
    );
  if (!/^[A-Z0-9]{4}-[A-Z0-9]{4}$/.test(how)) return null;
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(
        `Your Audit page: https://nomus.work/a/${room.slug}\nAccess code: ${how}`,
      );
      setCopied(true);
      onKept();
    } catch {
      setCopied(false);
    }
  };
  return (
    <div className={line} data-testid="room-keep">
      <p className="max-w-[520px]">
        This device is signed in. We could not email you just now, so keep this
        code. With the link to this page it opens it anywhere:{" "}
        <span
          className="tracking-[0.1em] text-black"
          data-testid="room-keep-code"
        >
          {how}
        </span>
      </p>
      <button
        type="button"
        onClick={copy}
        className="h-9 rounded-full bg-black px-4 text-[12px] font-medium text-white transition-colors hover:bg-[#262626]"
      >
        {copied ? "Copied" : "Copy link and code"}
      </button>
    </div>
  );
}

// Before day 1 there are three steps, and the page shows only the one you are on.
const ONBOARD = ["Agreement", "Payment", "Day 1"];
function Steps({ at }) {
  return (
    <ol
      className="flex max-w-[520px] items-center gap-3"
      data-testid="room-steps"
    >
      {ONBOARD.map((name, i) => (
        <React.Fragment key={name}>
          {i > 0 && (
            <li
              aria-hidden="true"
              className={`h-px flex-1 ${i <= at ? "bg-black/50" : "bg-black/15"}`}
            />
          )}
          <li
            aria-current={i === at ? "step" : undefined}
            className={`flex items-center gap-2.5 text-[13px] ${i > at ? "text-black/40" : "text-black"}`}
          >
            <span
              className={`flex h-6 w-6 items-center justify-center rounded-full text-[11px] tabular-nums ${i < at ? "bg-black/10" : i === at ? "bg-black text-white" : "ring-1 ring-black/20"}`}
            >
              {i < at ? <Tick size={12} /> : i + 1}
            </span>
            {name}
          </li>
        </React.Fragment>
      ))}
    </ol>
  );
}

// The top of the page, in the same light dress as the rest of the site. It
// always answers one question: what happens now.
function Masthead({ room, onLock, onInvite, fresh, onKept, children }) {
  return (
    <section className="relative bg-[#f7f7f7] px-5 pb-10 pt-24 sm:pb-14 sm:pt-28 print:hidden">
      <div className="absolute inset-0 overflow-hidden">
        <ShaderBackground strength={0.36} lines={11} fadeLeft={0.7} />
      </div>
      <div className="relative z-10 mx-auto max-w-[1400px]">
        <div className="flex items-center justify-between gap-3">
          <Pill white>{room.client}</Pill>
          <Access room={room} onLock={onLock} onInvite={onInvite} />
        </div>
        {children}
        {fresh && <Way room={room} how={fresh} onKept={onKept} />}
      </div>
    </section>
  );
}

// The agreement as a document: one sheet, read top to bottom.
function Paper({ a }) {
  return (
    <article
      className="rounded-[28px] bg-[#f7f7f7] p-7 sm:p-12 print:rounded-none print:bg-white print:p-0"
      data-testid="room-agreement"
    >
      <header className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="nm-title text-[28px] leading-[1.05] sm:text-[36px]">
          Audit agreement
        </h2>
        <p className="text-[12px] tabular-nums opacity-45">
          Version of {day(a.version)}
        </p>
      </header>
      <p className="mt-6 max-w-[640px] text-[16px] leading-[1.55]">
        Between {a.seller} (&ldquo;we&rdquo;), and {a.client}{" "}
        (&ldquo;you&rdquo;), a business.
      </p>
      <dl className="mt-6 grid gap-x-10 gap-y-4 border-y border-black/10 py-5 text-[16px] sm:grid-cols-2">
        <div>
          <dt className={`${LABEL} opacity-45`}>The job</dt>
          <dd className="mt-1">{a.job}</dd>
        </div>
        <div>
          <dt className={`${LABEL} opacity-45`}>The fee</dt>
          <dd className="mt-1">{a.fee}</dd>
        </div>
      </dl>
      <ol className="mt-8 space-y-6">
        {a.clauses.map((c, i) => (
          <li
            key={c.head}
            className="grid grid-cols-[2.25rem_1fr] break-inside-avoid"
          >
            <span className="pt-[3px] text-[12px] tabular-nums opacity-35">
              {two(i + 1)}
            </span>
            <div className="max-w-[640px]">
              <p className="text-[16px] font-medium leading-[1.4]">{c.head}</p>
              <p className="mt-1 text-[16px] leading-[1.55] opacity-70">
                {c.text}
              </p>
            </div>
          </li>
        ))}
      </ol>
      {a.signed && (
        <footer
          className="mt-10 border-t border-black/10 pt-6"
          data-testid="room-signed"
        >
          <p className={`${LABEL} flex items-center gap-1.5 opacity-45`}>
            <Tick size={12} /> Signed for {a.client.split(",")[0]}
          </p>
          <p className="mt-2 text-[20px] leading-[1.3] tracking-[-0.015em]">
            {a.signed.name}, {a.signed.role}
          </p>
          <p className="mt-1 text-[14px] opacity-55">{stamp(a.signed.at)}</p>
          <p className="mt-3 break-all text-[11px] opacity-35">
            Record of the exact text signed: {a.signed.hash}
          </p>
        </footer>
      )}
    </article>
  );
}

// Step one. The sheet on one side, the signature on the other.
function Sign({ room, onSign, onInvite }) {
  const a = room.agreement;
  const firm = a.client.split(",")[0];
  // The person who booked is filled in. A colleague brought in to sign starts
  // with their own address and an empty name.
  const booker =
    !room.me?.email ||
    room.me.email === (room.contact?.email || "").toLowerCase();
  const [who, setWho] = useState({
    name: booker ? room.contact?.name || "" : "",
    role: "",
    email: booker ? room.contact?.email || "" : room.me.email,
  });
  const [agree, setAgree] = useState(false);
  // "Not the one who signs?": the page goes to that person instead.
  const [other, setOther] = useState({ open: false, email: "", state: "" });
  const pass = async () => {
    if (!other.email.trim() || other.state === "busy") return;
    setOther((o) => ({ ...o, state: "busy" }));
    try {
      await onInvite(other.email.trim());
      setOther((o) => ({ ...o, state: "sent" }));
    } catch (err) {
      setOther((o) => ({
        ...o,
        state: err.message || "That did not work. Try again in a moment.",
      }));
    }
  };
  const [state, setState] = useState("idle");
  const [error, setError] = useState("");
  const set = (k) => (e) => setWho((w) => ({ ...w, [k]: e.target.value }));
  const minutes = Math.max(
    Math.round(
      a.clauses.reduce((n, c) => n + c.text.split(/\s+/).length, 0) / 200,
    ),
    1,
  );
  const send = async (e) => {
    e.preventDefault();
    if (state === "sending") return;
    setState("sending");
    setError("");
    try {
      await onSign({ ...who, agree });
    } catch (err) {
      setError(err.message || "That was not saved. Try again in a moment.");
      setState("idle");
    }
  };
  return (
    <section className="px-3 pt-3 sm:px-5 sm:pt-5 print:hidden">
      <div className="mx-auto grid max-w-[1400px] gap-3 lg:grid-cols-12 lg:gap-5">
        <div className="lg:col-span-8">
          <Paper a={a} />
        </div>
        <form
          onSubmit={send}
          className="self-start rounded-[28px] bg-black p-7 text-white sm:p-9 lg:sticky lg:top-24 lg:col-span-4"
          data-testid="room-sign"
        >
          <p className={`${LABEL} text-white/45`}>Sign for {firm}</p>
          <dl className="mt-5 space-y-3 border-b border-white/15 pb-5 text-[15px]">
            {[
              ["The job", a.job],
              ["The fee", `${a.fee}, paid once`],
              ["To read", `${a.clauses.length} clauses, about ${minutes} min`],
            ].map(([k, v]) => (
              <div key={k} className="flex justify-between gap-6">
                <dt className="shrink-0 text-white/45">{k}</dt>
                <dd className="text-right">{v}</dd>
              </div>
            ))}
          </dl>
          <div className="mt-5 space-y-2">
            <input
              aria-label="Your full name"
              placeholder="Your full name"
              autoComplete="name"
              value={who.name}
              onChange={set("name")}
              className={`${FIELD} text-black`}
            />
            <input
              aria-label="Your role"
              placeholder="Your role"
              autoComplete="organization-title"
              value={who.role}
              onChange={set("role")}
              className={`${FIELD} text-black`}
            />
            <input
              aria-label="Your work email"
              placeholder="Your work email"
              type="email"
              autoComplete="email"
              value={who.email}
              onChange={set("email")}
              className={`${FIELD} text-black`}
            />
          </div>
          <label className="mt-5 flex cursor-pointer items-start gap-3 text-[15px] leading-[1.4]">
            <input
              type="checkbox"
              checked={agree}
              onChange={(e) => setAgree(e.target.checked)}
              className="mt-[3px] h-4 w-4 shrink-0 accent-white"
            />
            I have read this agreement and I sign it for {firm}.
          </label>
          <button
            type="submit"
            disabled={
              !agree ||
              !who.name ||
              !who.role ||
              !who.email ||
              state === "sending"
            }
            className="mt-6 h-12 w-full rounded-full bg-white text-[14px] font-medium text-black transition-colors hover:bg-[#e6e6e6] disabled:opacity-35"
          >
            {state === "sending" ? "Signing…" : "Sign the agreement"}
          </button>
          {error && (
            <p role="alert" className="mt-3 text-[14px] text-red-300">
              {error}
            </p>
          )}
          <p className="mt-4 text-[12px] leading-[1.45] text-white/40">
            Nothing is charged yet. We record your name, role and email, the
            time, and your network address as proof of signing.
          </p>
          <div
            className="mt-6 border-t border-white/15 pt-5"
            data-testid="room-signer"
          >
            {other.state === "sent" ? (
              <p className="flex items-start gap-2 text-[14px] leading-[1.45] text-white/80">
                <Tick size={13} className="mt-[4px] shrink-0" />
                Sent to {other.email}. They get a link to this page and can sign
                there.
              </p>
            ) : other.open ? (
              <>
                <p className="text-[14px] leading-[1.45] text-white/70">
                  Who signs for {firm}? We email them a link to this page.
                </p>
                <div className="mt-3 flex gap-2">
                  <input
                    aria-label="Their work email"
                    placeholder="Their work email"
                    type="email"
                    value={other.email}
                    onChange={(e) =>
                      setOther((o) => ({
                        ...o,
                        email: e.target.value,
                        state: "",
                      }))
                    }
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        pass();
                      }
                    }}
                    className="h-11 min-w-0 flex-1 rounded-[12px] bg-white px-4 text-[15px] text-black outline-none placeholder:text-black/35"
                  />
                  <button
                    type="button"
                    onClick={pass}
                    disabled={!other.email.trim() || other.state === "busy"}
                    className="h-11 shrink-0 rounded-full bg-white px-5 text-[13px] font-medium text-black transition-colors hover:bg-[#e6e6e6] disabled:opacity-40"
                  >
                    {other.state === "busy" ? "Sending…" : "Send"}
                  </button>
                </div>
                {other.state && other.state !== "busy" && (
                  <p role="alert" className="mt-2 text-[13px] text-red-300">
                    {other.state}
                  </p>
                )}
              </>
            ) : (
              <button
                type="button"
                onClick={() => setOther((o) => ({ ...o, open: true }))}
                className="font-sans nm-link text-[14px] text-white/70"
              >
                Not the one who signs? Send this page to them
              </button>
            )}
          </div>
          <div className="mt-5 border-t border-white/15 pt-5">
            <Host dark line="A question before you sign?" />
          </div>
        </form>
      </div>
    </section>
  );
}

// Step two. One number, one button.
const ON_PAYING = (walk) => [
  walk
    ? "You pick a time for the walkthrough call, here on this page, from the next working day on."
    : "By the end of the next working day we write to you with two times for the walkthrough call.",
  "That day is day 1. The fourteen days run from it, not from today.",
  "If we think the Audit is wrong for you, we say so by the end of the next working day and refund the fee in full.",
];
function Pay({ room, confirming, waited, onCheck }) {
  const a = room.agreement;
  const [copied, setCopied] = useState(false);
  return (
    <section className="px-3 pt-3 sm:px-5 sm:pt-5 print:hidden">
      <div className="mx-auto grid max-w-[1400px] gap-3 lg:grid-cols-12 lg:gap-5">
        <div
          className="flex min-h-[340px] flex-col justify-between rounded-[28px] bg-[#f7f7f7] p-7 sm:p-12 lg:col-span-7"
          data-testid="room-pay-card"
        >
          <div>
            <p className={`${LABEL} opacity-45`}>The fee</p>
            <p className="nm-display mt-3 text-[72px] leading-[0.95] tracking-[-0.03em] sm:text-[112px]">
              {a.fee}
            </p>
            <p className="mt-4 max-w-[440px] text-[17px] leading-[1.45] opacity-65">
              Paid once, by card. It covers everything in the agreement. We
              charge nothing else.
            </p>
          </div>
          {confirming ? (
            <div className="mt-10" data-testid="room-confirming">
              <p className="text-[17px] leading-[1.45]">
                {waited
                  ? "We have not had the card company's confirmation yet. It usually takes a few seconds. If you have paid, do not pay again: check in a minute, or write to hello@nomus.work."
                  : "Waiting for the card company to confirm. This takes a few seconds."}
              </p>
              {waited && (
                <button
                  type="button"
                  onClick={onCheck}
                  className="mt-5 h-11 rounded-full bg-black px-7 text-[13px] font-medium text-white transition-colors hover:bg-[#262626]"
                >
                  Check again
                </button>
              )}
            </div>
          ) : (
            <div className="mt-10 flex flex-wrap items-center gap-x-5 gap-y-3">
              <a
                href={`${AUDIT.pay}?client_reference_id=${room.slug}`}
                data-testid="room-pay"
                className="inline-flex h-12 items-center gap-2 rounded-full bg-black px-8 text-[14px] font-medium text-white transition-colors hover:bg-[#262626]"
              >
                Pay {a.fee} <ArrowUpRight size={14} />
              </a>
              <span className="text-[13px] opacity-45">
                By card, through Stripe. Your receipt arrives by email.
              </span>
              <p
                className="basis-full text-[14px] leading-[1.45] opacity-60"
                data-testid="room-payer"
              >
                Someone else pays?{" "}
                <button
                  type="button"
                  onClick={async () => {
                    try {
                      await navigator.clipboard.writeText(
                        `${AUDIT.pay}?client_reference_id=${room.slug}`,
                      );
                      setCopied(true);
                    } catch {
                      setCopied(false);
                    }
                  }}
                  className="font-sans nm-link text-black"
                >
                  {copied ? "Copied" : "Copy the payment link"}
                </button>{" "}
                and send it to them. It is tied to this page: when they pay,
                this page moves on.
              </p>
            </div>
          )}
        </div>
        <div className="rounded-[28px] bg-[#f7f7f7] p-7 sm:p-10 lg:col-span-5">
          <p className={`${LABEL} opacity-45`}>The moment you pay</p>
          <ol className="mt-5">
            {ON_PAYING(room.walk_url).map((line, i) => (
              <li
                key={line}
                className="grid grid-cols-[2.25rem_1fr] border-t border-black/10 py-4 text-[16px] leading-[1.45] first:border-0 first:pt-0"
              >
                <span className="pt-[3px] text-[12px] tabular-nums opacity-35">
                  {two(i + 1)}
                </span>
                {line}
              </li>
            ))}
          </ol>
          <div className="mt-6 border-t border-black/10 pt-6">
            <Host line="Runs your Audit. Ask anything:" />
          </div>
        </div>
      </div>
    </section>
  );
}

// A box the client writes in. What they send is stored on the page at once.
function Write({ label, button, max, onSend, dark, initial = "" }) {
  const [value, setValue] = useState(initial);
  const [state, setState] = useState("idle");
  const [error, setError] = useState("");
  const send = async (e) => {
    e.preventDefault();
    if (!value.trim() || state === "sending") return;
    setState("sending");
    setError("");
    try {
      await onSend(value.trim());
      setValue("");
      setState("idle");
    } catch (err) {
      setError(err.message || "That was not saved. Try again in a moment.");
      setState("idle");
    }
  };
  return (
    <form onSubmit={send} className="mt-4" data-testid="room-write">
      <textarea
        aria-label={label}
        placeholder={label}
        rows={3}
        maxLength={max}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        className="w-full resize-y rounded-[14px] bg-white p-4 text-[16px] leading-[1.4] text-black outline-none ring-1 ring-black/10 placeholder:text-black/35 focus:ring-black"
      />
      <div className="mt-2 flex flex-wrap items-center gap-4">
        <button
          type="submit"
          disabled={!value.trim() || state === "sending"}
          className={`h-10 rounded-full px-6 text-[13px] font-medium transition-colors disabled:opacity-40 ${dark ? "bg-white text-black hover:bg-[#e6e6e6]" : "bg-black text-white hover:bg-[#262626]"}`}
        >
          {state === "sending" ? "Saving…" : button}
        </button>
        <span className="text-[12px] opacity-45">
          Saved on this page at once. We are told you wrote.
        </span>
      </div>
      {error && (
        <p
          role="alert"
          className={`mt-3 text-[14px] ${dark ? "text-red-300" : "text-red-600"}`}
        >
          {error}
        </p>
      )}
    </form>
  );
}

// The first thing after paying: pick the hour for the walkthrough, here.
// The calendar sits in a frame of its own and tells the page when a time is
// booked; the page keeps the time and nothing else.
const CALLS = {
  walk: {
    label: "With you · first",
    title: "Pick a time for the walkthrough.",
    text: "It is day 1 of your Audit: the fourteen days start then. An hour on a call, where the person who does the job shows it to us once, on a real case. If that is not you, forward them the invitation.",
  },
  readout: {
    label: "With you · the last call",
    title: "Pick a time for the readout.",
    text: "Forty-five minutes on day 14, or the first working day after it. We go through the written Audit together. If it says build, you also watch the first version do one real case of yours.",
  },
};
function Walk({ room, onReply, kind = "walk" }) {
  const about = CALLS[kind];
  const url = kind === "walk" ? room.walk_url : room.readout_url;
  const [tall, setTall] = useState(620);
  const [state, setState] = useState("idle");
  // Arranged outside the calendar: they tell the page when, since that day is
  // day 1.
  const [other, setOther] = useState(false);
  const [when, setWhen] = useState("");
  const origin = new URL(url).origin;
  // The page's id rides along, so the calendar can tell the site which page
  // a booking belongs to.
  const src = `${url}?${new URLSearchParams({
    embed: kind,
    theme: "dark",
    "metadata[room]": room.slug,
    ...(room.contact?.name && { name: room.contact.name }),
    ...(room.contact?.email && { email: room.contact.email }),
  })}`;
  const save = useCallback(
    async (when) => {
      setState("saving");
      try {
        await onReply({ [kind]: { when: when || "" } });
      } catch {
        setState("failed");
      }
    },
    [onReply, kind],
  );
  useEffect(() => {
    const hear = (e) => {
      if (e.origin !== origin || e.data?.originator !== "CAL") return;
      const type = String(e.data.type || "");
      if (type === "__dimensionChanged") {
        const h = Number(e.data.data?.iframeHeight);
        if (h > 300 && h < 2400) setTall(Math.ceil(h));
      }
      if (/bookingSuccessful/i.test(type))
        save(e.data.data?.startTime || e.data.data?.date || "");
    };
    window.addEventListener("message", hear);
    return () => window.removeEventListener("message", hear);
  }, [origin, save]);
  return (
    <section
      className="px-3 pt-3 sm:px-5 sm:pt-5 print:hidden"
      data-testid={`room-${kind}`}
    >
      <div className="mx-auto max-w-[1400px] rounded-[28px] bg-black p-3 text-white sm:p-5">
        <div className="grid gap-x-10 gap-y-3 p-4 sm:p-5 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <p className={`${LABEL} text-white/50`}>{about.label}</p>
            <h2 className="nm-title mt-3 text-[30px] leading-[1.08] sm:text-[44px]">
              {about.title}
            </h2>
          </div>
          <p className="self-end text-[16px] leading-[1.45] text-white/60 lg:col-span-5">
            {about.text}
          </p>
        </div>
        <div className="overflow-hidden rounded-[18px] bg-[#101010]">
          <iframe
            title={about.title}
            src={src}
            referrerPolicy="no-referrer"
            loading="lazy"
            style={{ height: tall }}
            className="block w-full border-0"
          />
        </div>
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2 px-4 pb-2 pt-4 text-[13px] text-white/50 sm:px-5">
          {state === "failed"
            ? "We could not note that here. Your booking itself is safe: the calendar has it."
            : "The calendar is run by Cal.com."}
          {other ? (
            <form
              className="flex flex-wrap items-center gap-2"
              onSubmit={(e) => {
                e.preventDefault();
                if (when) save(new Date(when).toISOString());
              }}
            >
              <label htmlFor="walk-when">When is it?</label>
              <input
                id="walk-when"
                type="datetime-local"
                value={when}
                onChange={(e) => setWhen(e.target.value)}
                className="h-9 rounded-[10px] bg-white px-3 text-[14px] text-black outline-none"
              />
              <button
                type="submit"
                disabled={!when || state === "saving"}
                className="h-9 rounded-full bg-white px-4 text-[12px] font-medium text-black transition-colors hover:bg-[#e6e6e6] disabled:opacity-40"
              >
                {state === "saving" ? "Saving…" : "Save"}
              </button>
            </form>
          ) : (
            <button
              type="button"
              onClick={() => setOther(true)}
              className="font-sans nm-link text-white/80"
              data-testid="room-walk-other"
            >
              Arranged it with us another way?
            </button>
          )}
          {!other && (
            <a
              href={`mailto:${HOST.email}?subject=${encodeURIComponent(`A time for the ${kind === "walk" ? "walkthrough" : "readout"}`)}`}
              className="nm-link text-white/80"
              data-testid="room-walk-none"
            >
              None of these times work?
            </a>
          )}
        </div>
      </div>
    </section>
  );
}

// One thing we need, which the client fills in where it stands. What the box
// asks for depends on the kind of thing it is.
const FILL = {
  person: "Name them",
  case: "Choose it",
  systems: "List them",
  "": "Answer",
};
const SMALL_FIELD =
  "h-11 w-full rounded-[12px] bg-white px-4 text-[15px] text-black outline-none ring-1 ring-black/10 placeholder:text-black/35 focus:ring-black";
function Need({ n, dark, paid, late, onReply, startOpen = false }) {
  const [open, setOpen] = useState(startOpen);
  const [v, setV] = useState({
    name: "",
    email: "",
    invite: true,
    reply: n.reply || "",
  });
  const [state, setState] = useState("idle");
  const [error, setError] = useState("");
  const set = (k) => (e) => setV((x) => ({ ...x, [k]: e.target.value }));
  const soft = dark ? "text-white/50" : "opacity-50";
  const line = dark ? "border-white/15" : "border-black/10";
  const listed = n.kind === "systems" && n.reply;
  const ready =
    n.kind === "person" ? v.name.trim() && v.email.trim() : v.reply.trim();
  const send = async (e) => {
    e.preventDefault();
    if (!ready || state === "busy") return;
    setState("busy");
    setError("");
    try {
      await onReply(
        n.kind === "person"
          ? {
              need: n.label,
              person: {
                name: v.name.trim(),
                email: v.email.trim(),
                invite: Boolean(paid && v.invite),
              },
            }
          : { need: n.label, reply: v.reply.trim() },
      );
      setOpen(false);
      setState("idle");
    } catch (err) {
      setError(err.message || "That was not saved. Try again in a moment.");
      setState("idle");
    }
  };
  return (
    <li className={`border-b ${line}`} data-testid="room-need">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className="group flex w-full items-start gap-4 py-4 text-left font-sans text-[17px] leading-[1.4]"
      >
        <span
          className={`mt-[3px] h-[18px] w-[18px] shrink-0 rounded-full ring-1 transition-colors ${dark ? "ring-white/40 group-hover:bg-white/20" : "ring-black/30 group-hover:bg-black/10"}`}
        />
        <span className="min-w-0 flex-1">{n.label}</span>
        <span
          className={`mt-[1px] inline-flex h-7 shrink-0 items-center rounded-full px-3 text-[12px] ring-1 transition-colors ${dark ? "ring-white/30 group-hover:bg-white group-hover:text-black" : "ring-black/20 group-hover:bg-black group-hover:text-white"}`}
        >
          {open ? "Close" : listed ? "Change" : FILL[n.kind || ""]}
        </span>
      </button>
      {late && !open && (
        <p
          className={`pb-4 pl-[34px] text-[14px] leading-[1.45] ${soft}`}
          data-testid="room-access-late"
        >
          Day 5 has passed. Your written Audit still arrives on day 14; the
          first version of the tool follows within seven days of the access.
        </p>
      )}
      {listed && !open && (
        <div className="pb-5 pl-[34px] text-[15px] leading-[1.5]">
          <p className="whitespace-pre-line">{n.reply}</p>
          <p className={`mt-3 max-w-[520px] ${soft}`}>
            Now let us look. In each one, invite {HOST.email} as a read-only
            user; it is often called viewer or accountant access. Never send a
            password. If a system cannot do that, add an export from it under
            Files, further down this page. We tick this off once we can see in.
          </p>
        </div>
      )}
      {open && (
        <form onSubmit={send} className="pb-5 pl-[34px]">
          {n.kind === "person" ? (
            <>
              <div className="grid gap-2 sm:grid-cols-2">
                <input
                  aria-label="Their name"
                  placeholder="Their name"
                  value={v.name}
                  onChange={set("name")}
                  className={SMALL_FIELD}
                />
                <input
                  aria-label="Their work email"
                  placeholder="Their work email"
                  type="email"
                  value={v.email}
                  onChange={set("email")}
                  className={SMALL_FIELD}
                />
              </div>
              {paid && (
                <label
                  className={`mt-3 flex cursor-pointer items-start gap-2.5 text-[14px] leading-[1.4] ${soft}`}
                >
                  <input
                    type="checkbox"
                    checked={v.invite}
                    onChange={(e) =>
                      setV((x) => ({ ...x, invite: e.target.checked }))
                    }
                    className={`mt-[3px] h-3.5 w-3.5 shrink-0 ${dark ? "accent-white" : "accent-black"}`}
                  />
                  Give them access to this page. We email them a link that signs
                  them in.
                </label>
              )}
            </>
          ) : n.kind === "case" ? (
            <>
              <input
                aria-label="The case"
                placeholder="For example: the September close for one entity"
                maxLength={300}
                value={v.reply}
                onChange={set("reply")}
                className={SMALL_FIELD}
              />
              <p className={`mt-2 text-[13px] leading-[1.4] ${soft}`}>
                A name or a reference is enough. We look at the case itself
                together on the walkthrough.
              </p>
            </>
          ) : (
            <>
              <textarea
                aria-label={
                  n.kind === "systems" ? "The systems" : "Your answer"
                }
                placeholder={
                  n.kind === "systems"
                    ? "One per line. For example:\nXero\nDext\nA shared spreadsheet"
                    : "Your answer"
                }
                rows={n.kind === "systems" ? 4 : 3}
                maxLength={1200}
                value={v.reply}
                onChange={set("reply")}
                className="w-full resize-y rounded-[12px] bg-white p-4 text-[15px] leading-[1.4] text-black outline-none ring-1 ring-black/10 placeholder:text-black/35 focus:ring-black"
              />
              {n.kind === "systems" && (
                <p className={`mt-2 text-[13px] leading-[1.4] ${soft}`}>
                  Only their names. No passwords, here or anywhere: we ask for
                  read-only access, and say how once we know the list.
                </p>
              )}
            </>
          )}
          <div className="mt-3 flex flex-wrap items-center gap-4">
            <button
              type="submit"
              disabled={!ready || state === "busy"}
              className={`h-10 rounded-full px-6 text-[13px] font-medium transition-colors disabled:opacity-40 ${dark ? "bg-white text-black hover:bg-[#e6e6e6]" : "bg-black text-white hover:bg-[#262626]"}`}
            >
              {state === "busy" ? "Saving…" : "Save"}
            </button>
            {error && (
              <span
                role="alert"
                className={`text-[14px] ${dark ? "text-red-300" : "text-red-600"}`}
              >
                {error}
              </span>
            )}
          </div>
        </form>
      )}
    </li>
  );
}

// The agreement allows one pause of up to fourteen days. While it lasts, this
// leads the page, with the one thing that ends it.
function Paused({ paused, onReply }) {
  const [state, setState] = useState("idle");
  return (
    <section
      className="px-3 pt-3 sm:px-5 sm:pt-5 print:hidden"
      data-testid="room-paused"
    >
      <div className="mx-auto grid max-w-[1400px] gap-x-10 gap-y-6 rounded-[28px] bg-black p-7 text-white sm:p-10 lg:grid-cols-12">
        <div className="lg:col-span-7">
          <p className={`${LABEL} text-white/50`}>
            Paused{paused.by === "client" ? " · at your request" : ""}
          </p>
          <h2 className="nm-title mt-3 text-[30px] leading-[1.08] sm:text-[44px]">
            Your Audit is paused until {ownDay(paused.until)}.
          </h2>
          {paused.reason && (
            <p className="mt-4 max-w-[560px] text-[17px] leading-[1.45] text-white/80">
              {paused.reason}
            </p>
          )}
        </div>
        <div className="flex flex-col justify-end lg:col-span-5">
          <p className="text-[16px] leading-[1.45] text-white/60">
            The days are not counted while it is paused, and the date your Audit
            is due moves with them. If it is not resumed by{" "}
            {ownDay(paused.until)}, the Audit is closed and you keep what we
            have written so far.
          </p>
          <div className="mt-5 flex flex-wrap items-center gap-4">
            <button
              type="button"
              disabled={state === "busy"}
              onClick={async () => {
                setState("busy");
                try {
                  await onReply({ resume: true });
                } catch {
                  setState("failed");
                }
              }}
              data-testid="room-resume"
              className="h-12 rounded-full bg-white px-8 text-[14px] font-medium text-black transition-colors hover:bg-[#e6e6e6] disabled:opacity-60"
            >
              {state === "busy" ? "Resuming…" : "Resume the Audit"}
            </button>
            {state === "failed" && (
              <span role="alert" className="text-[14px] text-red-300">
                That did not work. Try again in a moment.
              </span>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

// A closed Audit stays readable. The page says why it closed and what, if
// anything, can still happen.
const CLOSED = {
  lapsed: [
    "The pause ran out, so this Audit is closed.",
    "A pause lasts fourteen days at most. Everything written so far stays here. Within 90 days you can ask us to reopen it, once, when a place is free.",
  ],
  refunded: [
    "This Audit is closed, and the fee was refunded in full.",
    "Everything written so far stays here for you to read.",
  ],
  cancelled: [
    "This Audit is closed, as we agreed.",
    "Everything written so far stays here for you to read.",
  ],
  finished: [
    "This Audit is finished.",
    "The page stays open: the written Audit, the map and the record are yours to come back to.",
  ],
};
function Closed({ closed }) {
  const [head, text] = CLOSED[closed.reason] || CLOSED.cancelled;
  return (
    <section
      className="px-3 pt-3 sm:px-5 sm:pt-5 print:hidden"
      data-testid="room-closed"
    >
      <div className="mx-auto grid max-w-[1400px] gap-x-10 gap-y-6 rounded-[28px] bg-[#f7f7f7] p-7 sm:p-10 lg:grid-cols-12">
        <div className="lg:col-span-7">
          <p className={`${LABEL} opacity-45`}>
            {closed.reason === "finished" ? "Finished" : "Closed"} ·{" "}
            {ownDay(new Date(closed.at))}
          </p>
          <h2 className="nm-title mt-3 text-[30px] leading-[1.08] sm:text-[44px]">
            {head}
          </h2>
          <p className="mt-4 max-w-[560px] text-[17px] leading-[1.45] opacity-65">
            {text}
          </p>
        </div>
        <div className="flex flex-col justify-end lg:col-span-5">
          <Host
            line={
              closed.reason === "lapsed"
                ? "To reopen it, write to"
                : "Any question, write to"
            }
          />
        </div>
      </div>
    </section>
  );
}

// The middle of the page once the work has started: who holds the next move.
// The dark card is the side it is with.
const meeting = (date) =>
  `${date.toLocaleDateString("en-GB", {
    weekday: "long",
    day: "numeric",
    month: "long",
  })} at ${date.toLocaleTimeString("en-GB", {
    hour: "2-digit",
    minute: "2-digit",
  })}`;
function Now({ room, step, dayNo, onReply, onLog, later }) {
  const needs = room.needs.filter((n) => !n.done);
  const got = room.needs.filter((n) => n.done);
  const asked = room.questions.filter((q) => !q.answer);
  const yours = needs.length + asked.length;
  const latest = room.log.find((l) => l.from !== "client");
  const [answering, setAnswering] = useState(null);
  const [noting, setNoting] = useState(false);
  // A need they already filled in, opened again to correct it.
  const [changing, setChanging] = useState(null);
  // With the walkthrough still to book, that card leads and these stay quiet.
  const mine = yours > 0 && !later;
  const ours = !yours && !later;
  const soft = mine ? "text-white/50" : "opacity-45";
  const line = mine ? "border-white/15" : "border-black/10";
  const ring = mine ? "ring-white/40" : "ring-black/30";

  // What we are doing. Our own words if we wrote some; otherwise the page
  // says it from what it knows: above all, when the walkthrough is.
  const at = room.walk?.when ? new Date(room.walk.when) : null;
  const ahead = Boolean(
    room.walk && (!at || at.getTime() > Date.now() - 3600e3),
  );
  const walk = at
    ? `The walkthrough is on ${meeting(at)}, your time.`
    : "The walkthrough is booked. The time is in your calendar invite.";
  const us = room.next?.what
    ? {
        label: room.next.when,
        head: room.next.what,
        sub: ahead ? walk : "",
      }
    : ahead
      ? {
          label: "Walkthrough booked",
          head: at
            ? `We meet on ${meeting(at)}.`
            : "The walkthrough is booked.",
          sub: `${at ? "That is your time, and day 1 of the fourteen." : "The time is in your calendar invite. That day is day 1."} Until then we read what you have told us and prepare.`,
        }
      : later
        ? {
            label: "Today",
            head: "We read what you have told us and get ready for the walkthrough.",
          }
        : room.paid && !room.walk
          ? {
              label: "By the next working day",
              head: "We write to you with two times for the walkthrough call.",
            }
          : { label: step.when, head: step.what };
  const quiet = ours ? "text-white/50" : "opacity-45";
  return (
    <section className="px-3 pt-3 sm:px-5 sm:pt-5 print:hidden">
      <div className="mx-auto grid max-w-[1400px] gap-3 lg:grid-cols-2 lg:gap-5">
        <div
          className={`rounded-[28px] p-7 sm:p-10 ${mine ? "bg-black text-white" : "bg-[#f7f7f7]"}`}
          data-testid="room-yours"
        >
          <p className={`${LABEL} ${soft}`}>
            With you{later && yours ? " · after that" : ""}
          </p>
          <h2 className="nm-title mt-3 text-[30px] leading-[1.08] sm:text-[40px]">
            {yours
              ? yours === 1
                ? "One thing we need from you."
                : `${yours} things we need from you.`
              : "Nothing is needed from you right now."}
          </h2>
          {!yours && (
            <p className="mt-3 max-w-[440px] text-[16px] leading-[1.45] opacity-55">
              The moment that changes, it shows here.
            </p>
          )}
          {yours > 0 && (
            <ul className={`mt-7 border-t ${line}`}>
              {needs.map((n) => (
                <Need
                  key={n.label}
                  n={n}
                  dark={mine}
                  paid={Boolean(room.paid)}
                  late={n.kind === "systems" && dayNo > 5}
                  onReply={onReply}
                />
              ))}
              {asked.map((q) => (
                <li
                  key={q.q}
                  className={`border-b py-4 ${line}`}
                  data-testid="room-question"
                >
                  <div className="flex items-start gap-4">
                    <span
                      className={`mt-[3px] flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-full text-[11px] ring-1 ${ring}`}
                    >
                      ?
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="text-[17px] leading-[1.4]">{q.q}</p>
                      {q.who && (
                        <p className={`mt-1 text-[13px] ${soft}`}>
                          For {q.who}
                        </p>
                      )}
                      {answering === q.q ? (
                        <Write
                          dark={mine}
                          label="Your answer"
                          button="Send answer"
                          max={2000}
                          onSend={(answer) => onReply({ q: q.q, answer })}
                        />
                      ) : (
                        <button
                          type="button"
                          onClick={() => setAnswering(q.q)}
                          className={`mt-3 h-9 rounded-full px-5 text-[12px] font-medium transition-colors ${mine ? "bg-white text-black hover:bg-[#e6e6e6]" : "bg-black text-white hover:bg-[#262626]"}`}
                        >
                          Answer
                        </button>
                      )}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
          {(got.length > 0 || room.walk) && (
            <ul className="mt-5 space-y-1.5">
              {room.walk && (
                <li
                  className={`flex items-start gap-3 text-[14px] leading-[1.4] ${soft}`}
                  data-testid="room-walked"
                >
                  <Tick size={13} className="mt-[3px] shrink-0" />
                  Walkthrough booked
                </li>
              )}
              {got.map((n) =>
                changing === n.label ? (
                  <Need
                    key={n.label}
                    n={n}
                    dark={mine}
                    paid={Boolean(room.paid)}
                    startOpen
                    onReply={async (what) => {
                      await onReply(what);
                      setChanging(null);
                    }}
                  />
                ) : (
                  <li
                    key={n.label}
                    className={`flex items-start gap-3 text-[14px] leading-[1.4] ${soft}`}
                  >
                    <Tick size={13} className="mt-[3px] shrink-0" />
                    <span>
                      {n.label}
                      {n.reply && (
                        <span className="whitespace-pre-line">: {n.reply}</span>
                      )}
                      {n.reply && (
                        <button
                          type="button"
                          onClick={() => setChanging(n.label)}
                          className="font-sans nm-link ml-2"
                        >
                          Change
                        </button>
                      )}
                    </span>
                  </li>
                ),
              )}
            </ul>
          )}
          <div className="mt-7">
            {noting ? (
              <Write
                dark={mine}
                label="Your note"
                button="Add to the log"
                max={1200}
                onSend={async (note) => {
                  await onReply({ note });
                  setNoting(false);
                }}
              />
            ) : (
              <button
                type="button"
                onClick={() => setNoting(true)}
                className={`font-sans nm-link text-[14px] ${soft}`}
                data-testid="room-note"
              >
                Something we did not ask? Write us a note
              </button>
            )}
          </div>
        </div>

        <div
          className={`flex flex-col rounded-[28px] p-7 sm:p-10 ${ours ? "bg-black text-white" : "bg-[#f7f7f7]"}`}
          data-testid="room-next"
        >
          <p className={`${LABEL} ${quiet}`}>
            With us{us.label ? ` · ${us.label}` : ""}
          </p>
          <h2 className="nm-title mt-3 text-[30px] leading-[1.08] sm:text-[40px]">
            {us.head}
          </h2>
          {us.sub && (
            <p
              className="mt-3 max-w-[460px] text-[16px] leading-[1.45] opacity-60"
              data-testid="room-next-sub"
            >
              {us.sub}
            </p>
          )}
          <div className="mt-auto pt-10">
            {latest && (
              <div className="mb-8" data-testid="room-latest">
                <p className={`${LABEL} ${quiet}`}>
                  Latest{latest.date ? ` · ${day(latest.date, false)}` : ""}
                </p>
                <p className="mt-2 line-clamp-3 whitespace-pre-line text-[16px] leading-[1.45] opacity-75">
                  {latest.text}
                </p>
                <button
                  type="button"
                  onClick={onLog}
                  className={`font-sans nm-link mt-3 text-[14px] ${ours ? "text-white/55" : "opacity-55"}`}
                >
                  Read the whole log
                </button>
              </div>
            )}
            <div
              className={`border-t pt-6 ${ours ? "border-white/15" : "border-black/10"}`}
            >
              <Host dark={ours} line="Runs your Audit. Ask anything:" />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Block({ title, count, children }) {
  return (
    <section className="px-5 pt-16 lg:pt-24">
      <div className="mx-auto max-w-[1400px]">
        <h2 className="nm-title text-[30px] leading-[1.05] sm:text-[44px]">
          {title}
          {count != null && (
            <span className="ml-3 align-middle font-sans text-[14px] tabular-nums opacity-35">
              {count}
            </span>
          )}
        </h2>
        <div className="mt-8">{children}</div>
      </div>
    </section>
  );
}

// A client's page prints one thing at a time: the written Audit, or the
// agreement. Which one is said on the page itself, so the paper rules in
// index.css can leave the other out.
function printPaper(what) {
  document.documentElement.dataset.paper = what;
  window.print();
}

// The written Audit, once it exists. It then leads the page.
function Document({ result, client }) {
  // The full document, a page to a question, as in the sample.
  if (result.doc)
    return (
      <div className="nm-paper-audit pt-3 sm:pt-5" data-testid="room-result">
        <div className="px-5 pb-6 pt-8 print:hidden">
          <div className="mx-auto flex max-w-[1400px] flex-wrap items-end justify-between gap-x-8 gap-y-4">
            <div>
              <p className={`${LABEL} opacity-45`}>Your Audit</p>
              <p className="mt-2 text-[17px] leading-[1.4] text-black/60">
                {docPages(result.doc).length} pages. It is yours to keep.
                {result.heldUntil &&
                  ` The price in it is held until ${result.heldUntil}.`}
              </p>
            </div>
            <button
              type="button"
              onClick={() => printPaper("audit")}
              data-testid="room-result-print"
              className="inline-flex h-11 items-center gap-2 rounded-full bg-black px-6 text-[13px] font-medium text-white transition-colors hover:bg-[#262626]"
            >
              <Printer size={14} aria-hidden="true" /> Save as PDF
            </button>
          </div>
        </div>
        <AuditDoc doc={result.doc} client={client} />
      </div>
    );
  return (
    <section className="px-3 pt-3 sm:px-5 sm:pt-5" data-testid="room-result">
      <div className="mx-auto max-w-[1400px]">
        <div className="rounded-[28px] bg-[#f7f7f7] p-7 sm:p-12">
          <p className={`${LABEL} opacity-45`}>Your Audit</p>
          <p className="nm-display mt-3 text-[44px] leading-[0.98] tracking-[-0.03em] sm:text-[72px]">
            {result.verdict}
          </p>
          {result.text && (
            <p className="mt-6 max-w-[720px] text-[18px] leading-[1.4] opacity-70 sm:text-[21px]">
              {result.text}
            </p>
          )}
          <dl className="mt-10 grid gap-3 sm:grid-cols-3">
            {[
              ["Hours a month", result.hours],
              ["The tool, fixed", result.toolPrice],
              ["Price held until", result.heldUntil],
            ]
              .filter(([, v]) => v)
              .map(([k, v]) => (
                <div key={k} className="rounded-[18px] bg-white p-5">
                  <dt className="text-[12px] opacity-50">{k}</dt>
                  <dd className="nm-display mt-1 text-[28px] leading-none tracking-[-0.02em]">
                    {v}
                  </dd>
                </div>
              ))}
          </dl>
        </div>
        <div className="px-2 sm:px-7">
          {result.sections.map((s) => (
            <div
              key={s.title}
              className="grid gap-x-8 gap-y-3 border-b border-black/10 py-8 lg:grid-cols-12"
            >
              <h3 className="nm-title text-[24px] leading-[1.1] sm:text-[30px] lg:col-span-4">
                {s.title}
              </h3>
              <ul className="space-y-2.5 lg:col-span-8">
                {s.lines.map((line) => (
                  <li
                    key={line}
                    className="text-[17px] leading-[1.45] opacity-70"
                  >
                    {line}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// One line of the record. Closed until asked for.
function Row({ id, title, note, open, onToggle, children }) {
  return (
    <li id={`record-${id}`} className="scroll-mt-24 border-b border-black/10">
      <button
        type="button"
        onClick={() => onToggle(id)}
        aria-expanded={Boolean(open)}
        data-testid={`room-row-${id}`}
        className="flex w-full items-center justify-between gap-6 py-6 text-left sm:py-7"
      >
        <span className="text-[22px] leading-[1.15] tracking-[-0.01em] sm:text-[28px]">
          {title}
        </span>
        <span className="flex shrink-0 items-center gap-4 font-sans text-[14px] opacity-50">
          <span className="hidden sm:inline">{note}</span>
          <ChevronDown
            size={18}
            className={`transition-transform duration-300 ${open ? "rotate-180" : ""}`}
          />
        </span>
      </button>
      {open && <div className="pb-10">{children}</div>}
    </li>
  );
}

// Everything that has been said and done, kept out of the way.
function Record({ room, st, open, onToggle, onReply }) {
  // One of their own answers, opened again to correct it.
  const [editing, setEditing] = useState(null);
  const a = room.agreement;
  const answered = room.questions.filter((q) => q.answer);
  const paid = Boolean(room.paid);
  return (
    <section className="px-5 pt-16 lg:pt-24 print:hidden">
      <div className="mx-auto max-w-[1400px]">
        <p className={`${LABEL} opacity-45`}>The record</p>
        <ul className="mt-5 border-t border-black/10">
          {paid && (
            <Row
              id="days"
              title="The fourteen days"
              note={`Now: ${AUDIT_STEPS[st.stage].when.toLowerCase()}`}
              open={open.days}
              onToggle={onToggle}
            >
              <ol className="max-w-[900px]">
                {AUDIT_STEPS.slice(2).map((s, i) => {
                  const at = i + 2;
                  return (
                    <li
                      key={s.when}
                      aria-current={at === st.stage ? "step" : undefined}
                      className={`grid gap-x-8 gap-y-1 py-4 sm:grid-cols-12 ${at > st.stage ? "opacity-45" : ""}`}
                    >
                      <p className="flex items-center gap-2 text-[14px] tabular-nums sm:col-span-3 sm:pt-0.5">
                        {at < st.stage ? (
                          <Tick size={13} />
                        ) : (
                          <span
                            className={`h-2 w-2 rounded-full ${at === st.stage ? "bg-black" : "ring-1 ring-black/40"}`}
                          />
                        )}
                        {s.when}
                      </p>
                      <div className="sm:col-span-9">
                        <p className="text-[18px] leading-[1.3] tracking-[-0.01em]">
                          {s.what}
                        </p>
                        {at === st.stage && (
                          <p className="mt-1.5 text-[16px] leading-[1.5] opacity-60">
                            {s.text}
                          </p>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ol>
            </Row>
          )}
          {answered.length > 0 && (
            <Row
              id="answers"
              title="Questions and answers"
              note={`${answered.length} answered`}
              open={open.answers}
              onToggle={onToggle}
            >
              <ul>
                {answered.map((q) => (
                  <li
                    key={q.q}
                    className="grid gap-x-8 gap-y-2 py-4 lg:grid-cols-12"
                  >
                    <p className="text-[17px] leading-[1.35] opacity-55 lg:col-span-5">
                      {q.q}
                    </p>
                    <div className="lg:col-span-7">
                      {editing === q.q ? (
                        <Write
                          label="Your answer"
                          button="Save the change"
                          max={2000}
                          initial={q.answer}
                          onSend={async (answer) => {
                            await onReply({ q: q.q, answer });
                            setEditing(null);
                          }}
                        />
                      ) : (
                        <p className="whitespace-pre-line text-[17px] leading-[1.45]">
                          {q.answer}
                        </p>
                      )}
                      <p className="mt-1.5 text-[12px] opacity-40">
                        {q.from === "client" ? whose(q.by, room.me) : "Nomus"}
                        {q.at ? ` · ${day(q.at, false)}` : ""}
                        {q.from === "client" &&
                          !room.closed &&
                          editing !== q.q && (
                            <button
                              type="button"
                              onClick={() => setEditing(q.q)}
                              className="font-sans nm-link ml-3"
                              data-testid="room-edit-answer"
                            >
                              Edit
                            </button>
                          )}
                      </p>
                    </div>
                  </li>
                ))}
              </ul>
            </Row>
          )}
          {room.log.length > 0 && (
            <Row
              id="log"
              title="The log"
              note={`${room.log.length} ${room.log.length === 1 ? "entry" : "entries"}`}
              open={open.log}
              onToggle={onToggle}
            >
              <ol>
                {room.log.map((l, i) => (
                  <li
                    key={`${i}-${l.date}`}
                    className="grid gap-x-8 gap-y-1 py-4 lg:grid-cols-12"
                  >
                    <p className="text-[14px] tabular-nums opacity-45 lg:col-span-3 lg:pt-0.5">
                      {l.date ? day(l.date) : ""}
                      {l.from === "client" && (
                        <span className="ml-2 rounded-full bg-black px-2 py-0.5 text-[10px] tracking-[0.04em] text-white">
                          {whose(l.by, room.me)}
                        </span>
                      )}
                    </p>
                    <p className="whitespace-pre-line text-[17px] leading-[1.45] lg:col-span-9">
                      {l.text}
                    </p>
                  </li>
                ))}
              </ol>
            </Row>
          )}
          {a?.signed && (
            <Row
              id="agreement"
              title="The agreement"
              note={`Signed ${day(a.signed.at)}${paid ? ` · paid ${day(room.paid.at)}` : ""}`}
              open={open.agreement}
              onToggle={onToggle}
            >
              <div className="max-w-[900px]">
                <Paper a={a} />
                <button
                  type="button"
                  onClick={() => printPaper("agreement")}
                  className="mt-4 h-10 rounded-full px-6 text-[13px] font-medium ring-1 ring-black/20 transition-colors hover:bg-black hover:text-white"
                >
                  Print or save a copy
                </button>
              </div>
            </Row>
          )}
        </ul>
      </div>
    </section>
  );
}

function Page({
  room,
  onLock,
  onInvite,
  onReply,
  onSign,
  fresh,
  onKept,
  returned,
  waited,
  onCheck,
}) {
  const st = standing(room);
  const step = AUDIT_STEPS[st.stage];
  const [open, setOpen] = useState({});
  // After printing, the next print starts from the page's own choice again.
  useEffect(() => {
    const reset = () => delete document.documentElement.dataset.paper;
    window.addEventListener("afterprint", reset);
    return () => window.removeEventListener("afterprint", reset);
  }, []);
  const toggle = (id) => setOpen((o) => ({ ...o, [id]: !o[id] }));
  const toLog = () => {
    setOpen((o) => ({ ...o, log: true }));
    window.setTimeout(
      () =>
        document
          .getElementById("record-log")
          ?.scrollIntoView({ behavior: "smooth" }),
      60,
    );
  };
  // Back from the card page before the confirmation has reached us.
  const confirming = returned && st.phase === "pay";
  // Paid, and the walkthrough has no time yet: that comes before anything else.
  const walking = Boolean(
    room.walk_url && room.paid && !room.walk && !room.result,
  );
  // From day 10 the last call wants a time too.
  const reading = Boolean(
    room.readout_url &&
    !room.readout &&
    !st.closed &&
    !st.paused &&
    (st.day || 0) >= 10,
  );
  // Paused or closed: that is what the top of the page says, not the day.
  const flag = st.closed
    ? {
        label: `Since ${ownDay(new Date(st.closed.at))}`,
        big: st.closed.reason === "finished" ? "Finished" : "Closed",
      }
    : st.paused
      ? { label: "Paused until", big: ownDay(st.paused.until) }
      : null;
  const top = { room, onLock, onInvite, fresh, onKept };
  const firm = room.agreement?.client.split(",")[0];
  return (
    <div
      data-testid="room-page"
      data-phase={st.phase}
      className={room.result?.doc ? "nm-has-audit" : undefined}
    >
      {st.phase === "sign" && (
        <>
          <Masthead {...top}>
            <div className="mt-10 sm:mt-14">
              <Steps at={0} />
              <h1 className="nm-title mt-8 text-[44px] leading-[0.98] sm:text-[80px]">
                Read and sign.
              </h1>
              <p className="mt-5 max-w-[560px] text-[19px] leading-[1.35] text-black/65 sm:text-[22px]">
                This is the whole agreement between {firm} and us. Nothing is
                charged until the next step.
              </p>
            </div>
          </Masthead>
          <Sign room={room} onSign={onSign} onInvite={onInvite} />
        </>
      )}

      {st.phase === "pay" && (
        <>
          <Masthead {...top}>
            <div className="mt-10 sm:mt-14">
              <Steps at={1} />
              <h1 className="nm-title mt-8 text-[44px] leading-[0.98] sm:text-[80px]">
                {confirming
                  ? "Thank you. One moment."
                  : "Signed. One step left."}
              </h1>
              <p className="mt-5 max-w-[560px] text-[19px] leading-[1.35] text-black/65 sm:text-[22px]">
                {confirming
                  ? "Your page moves on as soon as the payment is confirmed. Nothing more is needed from you."
                  : "Pay the fee, then pick the day we start. The fourteen days run from that day, not from the payment."}
              </p>
            </div>
          </Masthead>
          <Pay
            room={room}
            confirming={confirming}
            waited={waited}
            onCheck={onCheck}
          />
          <Record
            room={room}
            st={st}
            open={open}
            onToggle={toggle}
            onReply={onReply}
          />
        </>
      )}

      {st.phase === "work" && (
        <>
          <Masthead {...top}>
            <div className="mt-10 grid items-end gap-8 sm:mt-14 lg:grid-cols-12">
              <div className="lg:col-span-7">
                <p className={`${LABEL} text-black/45`}>
                  {returned && room.paid
                    ? "Payment received. Thank you."
                    : "Your Audit"}
                </p>
                <h1 className="nm-title mt-3 text-[40px] leading-[1] sm:text-[68px]">
                  {room.job || "Your Audit."}
                </h1>
              </div>
              <div className="lg:col-span-4 lg:col-start-9 lg:text-right">
                {(flag || st.before) && (
                  <p className={`${LABEL} mb-2 text-black/45`}>
                    {flag ? flag.label : st.start ? "Day 1 is" : "Paid"}
                  </p>
                )}
                <p
                  className={`nm-display leading-none tracking-[-0.03em] ${flag || st.before ? "text-[44px] sm:text-[60px]" : "text-[56px] sm:text-[76px]"}`}
                  data-testid="room-day"
                >
                  {flag
                    ? flag.big
                    : st.before
                      ? st.start
                        ? ownDay(st.start)
                        : "Pick your day"
                      : st.day == null
                        ? step.when
                        : `Day ${st.day}`}
                  {st.day != null && !flag && (
                    <span className="text-black/25"> of 14</span>
                  )}
                </p>
              </div>
            </div>
            {(st.day != null || st.before) && (
              <ol
                aria-hidden="true"
                className="mt-8 grid gap-[3px]"
                style={{ gridTemplateColumns: "repeat(14, minmax(0, 1fr))" }}
              >
                {Array.from({ length: 14 }, (_, i) => (
                  <li
                    key={i}
                    className={`h-[5px] rounded-full ${i + 1 === st.day ? "bg-black" : i + 1 < (st.day || 0) ? "bg-black/45" : "bg-black/10"}`}
                  />
                ))}
              </ol>
            )}
            <div className="mt-4 flex flex-wrap justify-between gap-x-8 gap-y-1 text-[14px] text-black/55">
              <p>
                <span className="text-black">
                  {st.day != null && flag ? `Day ${st.day} of 14` : step.when}
                </span>{" "}
                ·{" "}
                {st.closed
                  ? "This Audit is closed."
                  : st.paused
                    ? "The days are not counted while it is paused."
                    : st.before
                      ? "The fourteen days start on the day of the walkthrough."
                      : step.what}
              </p>
              {st.due && !room.result && !st.closed && (
                <p data-testid="room-due">
                  {st.paused
                    ? "Due, if resumed today:"
                    : "The document and the readout:"}{" "}
                  <span className="text-black">{ownDay(st.due)}</span>
                </p>
              )}
            </div>
          </Masthead>

          {st.closed && <Closed closed={st.closed} />}
          {st.paused && !st.closed && (
            <Paused paused={st.paused} onReply={onReply} />
          )}
          {room.result && (
            <Document
              result={room.result}
              client={room.agreement?.client.split(",")[0] || room.client}
            />
          )}
          {walking && !st.closed && <Walk room={room} onReply={onReply} />}
          {reading && <Walk room={room} onReply={onReply} kind="readout" />}
          {!st.closed && (
            <Now
              room={room}
              step={step}
              dayNo={st.day || 0}
              onReply={onReply}
              onLog={toLog}
              later={walking || Boolean(st.paused)}
            />
          )}

          {room.paid && <RoomFiles room={room} closed={Boolean(st.closed)} />}

          {room.map.length > 0 && (
            <div className="print:hidden">
              <Block title="The job, so far" count={`${room.map.length} steps`}>
                <ol className="border-t border-black/10">
                  {room.map.map((s, i) => (
                    <li
                      key={`${i}-${s.name}`}
                      className="grid gap-x-8 gap-y-1 border-b border-black/10 py-5 lg:grid-cols-12"
                    >
                      <span className="text-[12px] tabular-nums opacity-35 lg:col-span-1 lg:pt-1">
                        {two(i + 1)}
                      </span>
                      <p className="text-[19px] leading-[1.25] tracking-[-0.015em] lg:col-span-4">
                        {s.name}
                      </p>
                      <p className="text-[16px] leading-[1.45] opacity-60 lg:col-span-7">
                        {s.text}
                      </p>
                    </li>
                  ))}
                </ol>
              </Block>
            </div>
          )}
          <Record
            room={room}
            st={st}
            open={open}
            onToggle={toggle}
            onReply={onReply}
          />
        </>
      )}

      {/* Printing gives the agreement, whatever the page is showing. */}
      {room.agreement && (
        <div className="nm-paper-agreement hidden px-8 print:block">
          <Paper a={room.agreement} />
        </div>
      )}

      <p className="px-5 pb-10 pt-16 text-center text-[12px] opacity-40 print:hidden">
        Private to {room.client}. Last updated {day(room.updated_at)}. A person
        answers at hello@nomus.work.
      </p>
    </div>
  );
}

const NO_COOKIE =
  "This browser is not keeping you signed in. It may be blocking cookies for this site. Allow cookies for nomus.work, then enter your work email and we send a fresh link.";

export default function Room() {
  const { slug } = useParams();
  const [params, setParams] = useSearchParams();
  // Sent back here by the card page after paying.
  const [returned] = useState(() => params.get("paid") === "1");
  const [room, setRoom] = useState(null);
  // The first look: is this device already signed in?
  const [looking, setLooking] = useState(true);
  const [notice, setNotice] = useState("");
  // Just booked: say once how they get back in.
  const [fresh, setFresh] = useState("");
  const [waited, setWaited] = useState(false);
  // Signed in, but the browser will not keep it.
  const [loose, setLoose] = useState(false);

  // A private page is never measured or recorded.
  useEffect(() => pauseTracking(), []);
  useEffect(() => {
    if (params.has("paid")) setParams({}, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const show = useCallback(
    (opened) => {
      remember(slug);
      setFresh(isFresh(slug));
      setRoom(opened);
    },
    [slug],
  );

  // A sign-in link opened in a tab that already shows this page changes only
  // the part after "#": look again when that happens.
  const [turn, setTurn] = useState(0);
  useEffect(() => {
    const again = () =>
      window.location.hash.startsWith("#in=") && setTurn((n) => n + 1);
    window.addEventListener("hashchange", again);
    return () => window.removeEventListener("hashchange", again);
  }, []);

  // A link from one of our letters carries a one-time token after "#", which
  // never leaves the browser. It is not spent by opening the page: company
  // mail systems open links to scan them, and that must not use it up. It is
  // spent when the person presses "Sign in".
  const [token, setToken] = useState("");
  useEffect(() => {
    let live = true;
    (async () => {
      const carried = /^#in=([A-Za-z0-9_-]{43})$/.exec(
        window.location.hash,
      )?.[1];
      if (carried)
        window.history.replaceState(
          null,
          "",
          window.location.pathname + window.location.search,
        );
      try {
        // Already signed in on this device: the link is not needed.
        const old = legacyCode(slug);
        const opened = await call(slug, "", old ? { code: old } : {});
        if (live) show(opened.room);
      } catch (e) {
        if (!live) return;
        if (carried) setToken(carried);
        else if (e.status === 429) setNotice(e.message);
        // They booked a moment ago and are already signed out: the browser
        // is not keeping our sign-in cookie.
        else if (isFresh(slug) || !navigator.cookieEnabled)
          setNotice(NO_COOKIE);
      } finally {
        if (live) setLooking(false);
      }
    })();
    return () => {
      live = false;
    };
  }, [slug, show, turn]);
  const enter = async () => {
    try {
      show((await call(slug, "enter", { token })).room);
      // Signed in, but will it hold? If the very next request is turned
      // away, the browser is blocking the cookie: say so on the page.
      call(slug).catch((e) => e.status === 401 && setLoose(true));
    } catch (e) {
      setNotice(
        e.status === 401
          ? "That link has expired or was already used. Enter your work email and we send a fresh one."
          : e.message || "That did not work. Ask for a fresh link below.",
      );
    } finally {
      setToken("");
    }
  };

  // Back from paying: ask until the confirmation has reached the page.
  const unpaid = Boolean(room) && !room.paid;
  useEffect(() => {
    if (!returned || !unpaid) return undefined;
    let tries = 0;
    let live = true;
    const id = window.setInterval(async () => {
      tries += 1;
      try {
        const now = await call(slug);
        if (live) setRoom(now.room);
      } catch {
        // Ask again on the next turn.
      }
      if (tries >= 15) {
        window.clearInterval(id);
        if (live) setWaited(true);
      }
    }, 2000);
    return () => {
      live = false;
      window.clearInterval(id);
    };
  }, [returned, unpaid, slug]);

  const leave = async () => {
    try {
      await call(slug, "leave");
    } catch {
      // The device forgets the page either way.
    }
    forgetRoom(slug);
    setRoom(null);
    setNotice("");
    window.scrollTo({ top: 0 });
  };
  const act = (action) => async (what) =>
    setRoom((await call(slug, action, what)).room);
  const sign = async (who) => {
    await act("sign")(who);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  if (room)
    return (
      <>
        {loose && (
          <p
            role="alert"
            className="fixed inset-x-0 bottom-0 z-50 bg-black px-5 py-3 text-center text-[14px] leading-[1.4] text-white print:hidden"
            data-testid="room-loose"
          >
            This browser is not keeping you signed in, so what you write here
            may not be saved. Allow cookies for nomus.work and reload.
          </p>
        )}
        <Page
          room={room}
          onLock={leave}
          onInvite={(email) => act("invite")({ email })}
          onReply={act("reply")}
          onSign={sign}
          fresh={fresh}
          onKept={() => clearFresh(slug)}
          returned={returned}
          waited={waited}
          onCheck={async () => {
            try {
              setRoom((await call(slug)).room);
            } catch {
              // The page stays as it is; they can try again.
            }
          }}
        />
      </>
    );
  // Until we know whether this device is signed in, show neither the door nor
  // the page.
  if (looking)
    return <section className="min-h-screen bg-black" aria-busy="true" />;
  if (token) return <Arrive onEnter={enter} />;
  return (
    <Gate
      notice={notice}
      onLink={(email) => call(slug, "link", { email })}
      onCode={async (code) => show((await call(slug, "", { code })).room)}
    />
  );
}
